import { useCallback, useEffect, useState } from "react";
import { addRti, getAllRti, updateRti } from "../../services/AppService";
import RtiFormModal from "./RtiFormModal";
import ViewRtiDetailsModal from "./ViewRtiDetailsModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import PageFade from "../../components/Login/PageFade";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import closeButton from "../../assets/images/close.png";

const Rti = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [selectedRti, setSelectedRti] = useState(null);
    const [rtis, setRti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);


    const [editingRti, setEditingRti] = useState(null);

    const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchRti = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getAllRti({ search: debouncedSearch, page, size });
            setRti(data?.content || []);
        } catch (error) {
            console.error("Failed to fetch RTI", error);
            if (error.response?.status !== 404) {
            showError("Failed to load RTI records");
        } else {
            // If it is a 404, just ensure the list is empty so the table shows "No records found"
            setRti([]); 
        }
        } finally {
            setLoading(false);
        }
    }, [page, size, debouncedSearch]);

    useEffect(() => { fetchRti(); }, [fetchRti]);

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({ open: true, formData: formData });
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            if (editingRti) {
                await updateRti(editingRti.id, formData);
                showSuccess("Rti details updated successfully");
            } else {
                await addRti(formData);
                showSuccess("Rti details registered successfully");
            }
            fetchRti();
            setConfirmModal({ open: false, formData: null });
            setEditingRti(null);
        } catch (error) {
            showError(error.response?.data?.message || "Internal Server Error");
        }
    }

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue">{t('pageTitles.rtiList')}</h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                </div>

                <div className="text-end mb-2">
                    <button className="btnAdd" onClick={() => { setEditingRti(null); setIsModalOpen(true); }}>
                        {t('common.addNew')}
                    </button>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm">{t('common.show')}</label>
                        <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm">
                            {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <label className="text-sm">{t('common.entries')}</label>
                    </div>
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        className="border rounded px-3 py-1 text-sm outline-none focus:border-blue-400"
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white text-sm">
                                <th className="px-3 py-2 text-center border-b border-black w-16">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 border-b border-black text-left">{t('complaint.complainerName')}</th>
                                <th className="px-3 py-2 border-b border-black text-left">{t('rti.rtiAgainst')}</th>
                                <th className="px-3 py-2 border-b border-black text-left">{t('complaint.department')}</th>
                                <th className="px-3 py-2 text-center border-b border-black w-20">{t('tables.view')}</th>
                                <th className="px-3 py-2 text-center border-b border-black w-20">{t('tables.edit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-4">{t('common.loading')}</td></tr>
                            ) : rtis.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-4">{t('common.noRecords')}</td></tr>
                            ) : (
                                rtis.map((item, idx) => (
                                    <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100 transition-colors`}>
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2 font-medium">{item.complainerName}</td>
                                        <td className="px-3 py-2 text-slate-600">{item.rtiAgainst}</td>
                                        <td className="px-3 py-2 text-slate-600">{item.departmentName}</td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 btnImgView py-1 rounded text-xs" onClick={() => { setSelectedRti(item); setIsViewModalOpen(true); }}></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 py-1 btnImgEdit rounded text-xs" onClick={() => { setEditingRti(item); setIsModalOpen(true); }}></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <RtiFormModal
                    isOpen={isModalOpen}
                    mode={editingRti ? 'edit' : 'add'}
                    editData={editingRti}
                    onSubmit={handleFormSubmit}
                    onClose={() => { setIsModalOpen(false); setEditingRti(null); }}
                />

                <ViewRtiDetailsModal
                    isOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    rti={selectedRti}
                />

                <ConfirmActionModal
                    open={confirmModal.open}
                    title={editingRti ? "Confirm Update" : "Confirm Add"}
                    message={`Are you sure you want to ${editingRti ? "update" : "save"} this RTI detail?`}
                    onConfirm={handleConfirmSave}
                    onClose={() => setConfirmModal({ open: false, formData: null })}
                />

                <MessageModal
                    open={messageModal.open}
                    onClose={() => setMessageModal({ ...messageModal, open: false })}
                    {...messageModal}
                />
            </div>
        </PageFade>
    )
}

export default Rti;